import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setGeolocation, setGoogleScriptLoaded } from '../../features/geolocationSlice';
import {
  isGoogleMapsMapReady,
  isGoogleMapsPlacesReady,
  loadGoogleMapsMapLibrary,
  loadGoogleMapsPlacesLibrary,
} from 'lib/utils/googleMaps/loadGoogleMapsMapLibrary';

export interface GeoLocation {
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
}

interface GeolocationHookResult {
  location: GeoLocation | null;
  error: string | null;
  isLoaded: boolean;
}

const useGeolocation = (): GeolocationHookResult => {
  const [location, setLocationState] = useState<GeoLocation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;

    const ensureMapsLibraries = async (): Promise<void> => {
      if (isGoogleMapsMapReady()) {
        if (!cancelled) {
          setIsLoaded(true);
        }
        return;
      }

      let mapApiKey: string | undefined;
      try {
        mapApiKey = siteSettings?.googleMapsAPIKey?.value
          ? atob(siteSettings.googleMapsAPIKey.value)
          : undefined;
      } catch {
        mapApiKey = undefined;
      }

      if (!mapApiKey) {
        return;
      }

      try {
        if (!isGoogleMapsPlacesReady()) {
          await loadGoogleMapsPlacesLibrary(mapApiKey);
        }
        if (cancelled) {
          return;
        }

        if (!isGoogleMapsMapReady()) {
          await loadGoogleMapsMapLibrary(mapApiKey);
        }
        if (cancelled) {
          return;
        }

        if (isGoogleMapsMapReady()) {
          setIsLoaded(true);
        }
      } catch {
        // Leave isLoaded false when Places/Maps cannot load.
      }
    };

    void ensureMapsLibraries();

    return () => {
      cancelled = true;
    };
  }, [siteSettings?.googleMapsAPIKey?.value]);

  useEffect(() => {
    if (isLoaded && !sitecoreContext?.pageEditing) {
      dispatch(setGoogleScriptLoaded());
    }
  }, [isLoaded, sitecoreContext?.pageEditing, dispatch]);

  useEffect(() => {
    let cancelled = false;

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    if (!isLoaded || sitecoreContext?.pageEditing) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        if (cancelled) {
          return;
        }

        try {
          if (typeof window.google?.maps?.Geocoder !== 'function') {
            return;
          }

          const geocoder = new google.maps.Geocoder();
          const latLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          const response = await geocoder.geocode({ location: latLng });

          if (cancelled) {
            return;
          }

          if (response.results[0]) {
            const addressComponents = response.results[0].address_components;
            const locationData: GeoLocation = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              address: response.results[0].formatted_address,
              city: addressComponents.find((component) => component.types.includes('locality'))
                ?.long_name,
              state: addressComponents.find((component) =>
                component.types.includes('administrative_area_level_1')
              )?.long_name,
              country: addressComponents.find((component) => component.types.includes('country'))
                ?.long_name,
              zipcode: addressComponents.find((component) =>
                component.types.includes('postal_code')
              )?.long_name,
            };

            setLocationState(locationData);
            dispatch(
              setGeolocation({
                location: {
                  latitude: locationData.latitude,
                  longitude: locationData.longitude,
                  zipcode: locationData.zipcode ?? null,
                  city: locationData.city ?? null,
                  state: locationData.state ?? null,
                },
                error: null,
              })
            );
          }
        } catch (err) {
          if (cancelled) {
            return;
          }
          setError('Error getting location details');
        }
      },
      (err) => {
        if (cancelled) {
          return;
        }
        setError(`Error getting location: ${err.message}`);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [isLoaded, sitecoreContext?.pageEditing, dispatch]);

  return { location, error, isLoaded };
};

export default useGeolocation;
